import { useState } from "react";
import Button from "ui/buttons/Button";
import { Tooltip } from "./Tooltip";

export function Wrapper({ children }) {
  return <Tooltip.Provider>{children}</Tooltip.Provider>;
}

<Wrapper>

## Basic Tooltip

<Tooltip.Root>
  <Tooltip.Trigger asChild>
    <Button btnStyle="secondary">Hover over me</Button>
  </Tooltip.Trigger>
  <Tooltip.Portal>
    <Tooltip.Content>
      <Tooltip.Arrow />
      This is a basic tooltip
    </Tooltip.Content>
  </Tooltip.Portal>
</Tooltip.Root>

---

## Positions

<div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px", maxWidth: "300px" }}>
  <Tooltip.Root>
    <Tooltip.Trigger asChild>
      <Button btnStyle="secondary" style={{ width: "100%" }}>
        Top
      </Button>
    </Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content side="top">
        <Tooltip.Arrow />
        Top tooltip
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
  <Tooltip.Root>
    <Tooltip.Trigger asChild>
      <Button btnStyle="secondary" style={{ width: "100%" }}>
        Right
      </Button>
    </Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content side="right">
        <Tooltip.Arrow />
        Right tooltip
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
  <Tooltip.Root>
    <Tooltip.Trigger asChild>
      <Button btnStyle="secondary" style={{ width: "100%" }}>
        Bottom
      </Button>
    </Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content side="bottom">
        <Tooltip.Arrow />
        Bottom tooltip
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
  <Tooltip.Root>
    <Tooltip.Trigger asChild>
      <Button btnStyle="secondary" style={{ width: "100%" }}>
        Left
      </Button>
    </Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content side="left">
        <Tooltip.Arrow />
        Left tooltip
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
</div>

---

## Rich Content

<Tooltip.Root>
  <Tooltip.Trigger asChild>
    <Button btnStyle="secondary">Rich tooltip</Button>
  </Tooltip.Trigger>
  <Tooltip.Portal>
    <Tooltip.Content style={{ maxWidth: "250px" }}>
      <Tooltip.Arrow />
      <div>
        <strong>Important Information</strong>
        <p style={{ margin: "8px 0 0", fontSize: "12px" }}>Tooltip with formatted content.</p>
      </div>
    </Tooltip.Content>
  </Tooltip.Portal>
</Tooltip.Root>

---

## Controlled

export function ControlledTooltip() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
      <Tooltip.Root open={open} onOpenChange={setOpen}>
        <Tooltip.Trigger asChild>
          <Button btnStyle="secondary">Controlled trigger</Button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content>
            <Tooltip.Arrow />
            Controlled tooltip
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
      <Button btnStyle="tertiary" onClick={() => setOpen(!open)}>
        Toggle: {open ? "Hide" : "Show"}
      </Button>
    </div>
  );
}

<ControlledTooltip />

---

## Delay Options

<div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
  <Tooltip.Root delayDuration={0}>
    <Tooltip.Trigger asChild>
      <Button btnStyle="secondary">No delay</Button>
    </Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content>
        <Tooltip.Arrow />
        Instant (0ms)
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
  <Tooltip.Root delayDuration={500}>
    <Tooltip.Trigger asChild>
      <Button btnStyle="secondary">500ms</Button>
    </Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content>
        <Tooltip.Arrow />
        Half second
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
  <Tooltip.Root delayDuration={1000}>
    <Tooltip.Trigger asChild>
      <Button btnStyle="secondary">1s</Button>
    </Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content>
        <Tooltip.Arrow />
        One second
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
</div>

---

## On Disabled Button

Wrap in a span to show tooltip on disabled elements:

<Tooltip.Root>
  <Tooltip.Trigger asChild>
    <span style={{ display: "inline-block" }}>
      <Button btnStyle="primary" disabled style={{ pointerEvents: "none" }}>
        Disabled button
      </Button>
    </span>
  </Tooltip.Trigger>
  <Tooltip.Portal>
    <Tooltip.Content>
      <Tooltip.Arrow />
      This action is unavailable
    </Tooltip.Content>
  </Tooltip.Portal>
</Tooltip.Root>

---

## Inline Text

Hover over the <Tooltip.Root><Tooltip.Trigger asChild><span style={{ textDecoration: "underline dotted", cursor: "help" }}>highlighted text</span></Tooltip.Trigger><Tooltip.Portal><Tooltip.Content><Tooltip.Arrow />Inline help</Tooltip.Content></Tooltip.Portal></Tooltip.Root> to see the tooltip. Also works with <Tooltip.Root><Tooltip.Trigger asChild><abbr style={{ cursor: "help" }}>API</abbr></Tooltip.Trigger><Tooltip.Portal><Tooltip.Content><Tooltip.Arrow />Application Programming Interface</Tooltip.Content></Tooltip.Portal></Tooltip.Root> abbreviations.

</Wrapper>
